/*
 * SlavicAccentCore — stress marks for Ukrainian and Belarusian text, in the browser.
 *
 * Pure logic, no DOM: the page (/slavic-stress) and the node tests share it.
 * Data: public/vendor/slavic-accent/<lang>-v1.txt (built by scripts/slavic-stress/build.py,
 * CC-BY-SA-4.0 — Wiktionary via kaikki.org for Ukrainian, GrammarDB for Belarusian).
 *
 * Decisions, in order, for every word:
 *   1. a mark the user typed (U+0301 after the vowel or '+' before it) is LAW;
 *   2. dictionary: a single reading, or a homograph ranked best-first, then
 *      three context rules — measured on the UNLP-2025 Ukrainian benchmark
 *      (1026 sentences, 2026-09-24), each kept only because it helped:
 *        func  — a frequent form whose reading is a pronoun/adverb/conjunction
 *                beats a noun reading («тому́», not «то́му»)          +0.5 pt
 *        prep  — after a preposition that governs the genitive, the
 *                genitive-singular reading («біля вікна́»)          +0.2 pt
 *        loc   — after в/у/на/о/при, an inflected reading beats an adverb or
 *                conjunction one («в то́му, що», but «тому́ що»)
 *        agree — the reading that agrees in case/number with the adjective,
 *                pronoun or numeral before it («великі ві́кна»)      +0.3 pt
 *      capitalisation picks proper-name readings (Украї́на vs укра́їна);
 *   3. unknown word: the ending model guesses (flagged 'guess', ~86% right);
 *   4. otherwise the word is left bare and flagged 'none'.
 * Benchmark result with this file's logic: 87.9% of polysyllabic words right,
 * 75.1% on heteronyms (best published system: 64.3% on ambiguous words).
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.SlavicAccentCore = factory();
})(typeof globalThis !== 'undefined' ? globalThis : (typeof self !== 'undefined' ? self : this), function () {
    'use strict';

    var VOWELS = { uk: 'аеєиіїоуюяАЕЄИІЇОУЮЯ', be: 'аеёіоуыэюяАЕЁІОУЫЭЮЯ' };
    var ACUTE = '́';
    // Letters of both alphabets (a Ukrainian text may quote Belarusian and back).
    var LETTER = 'А-Яа-яЁёЄєІіЇїҐґЎў';
    // A word: letters, apostrophes inside it (', ’, ʼ, `), combining accents after
    // vowels, '+' before a vowel. Hyphens are handled by the tokenizer, not here.
    var WORD_RE = new RegExp('\\+?[' + LETTER + '](?:[' + LETTER + '\'’ʼ`‘́̀]|\\+(?=[' + LETTER + ']))*', 'g');
    var APOS_RE = /[’ʼ`‘]/g;
    var FUNC_POS = { pron: 1, det: 1, conj: 1, prep: 1, particle: 1, adv: 1, num: 1, intj: 1, parenth: 1, pred: 1 };
    var AGREE_POS = { adj: 1, det: 1, pron: 1, num: 1, participle: 1 };
    var GEN_PREPS = {
        uk: toSet('до від біля коло без для серед посеред край крім окрім замість після протягом щодо навколо довкола вздовж уздовж позаду попереду поблизу внаслідок заради з із зі зо'),
        be: toSet('да ад каля ля без для сярод пасярод акрамя апроч замест пасля вакол уздоўж ўздоўж наконт дзеля накшталт з са')
    };
    // Prepositions that take the locative (and, most of them, the accusative).
    var LOC_PREPS = { uk: toSet('в у на о об при по'), be: toSet('у ў на аб пры па') };
    var INVARIABLE_POS = { adv: 1, conj: 1 };

    function toSet(s) { var o = {}; s.split(/\s+/).forEach(function (w) { if (w) o[w] = 1; }); return o; }
    function normKey(s) { return s.replace(APOS_RE, "'").toLowerCase(); }
    function isVowel(lang, ch) { return VOWELS[lang].indexOf(ch) >= 0; }
    function vowelIdx(lang, base) {
        var out = [];
        for (var i = 0; i < base.length; i++) if (isVowel(lang, base[i])) out.push(i);
        return out;
    }

    /* ── dictionary ──────────────────────────────────────────────────────── */

    function parseDict(text) {
        var head = text.slice(0, text.indexOf('\n'));
        if (head.indexOf('#SLAVIC-ACCENT') !== 0) throw new Error('slavic-accent: not a dictionary');
        var meta = {};
        head.split(' ').slice(1).forEach(function (kv) { var p = kv.split('='); meta[p[0]] = p[1]; });
        var block = +meta.block || 64;
        var fStart = text.indexOf('\n@F\n') + 4;
        var hStart = text.indexOf('\n@H\n');
        var sStart = text.indexOf('\n@S\n');
        var forms = text.slice(fStart, hStart + 1);        // keeps the trailing '\n'
        // Block index: offsets of every `block`-th line and its (full) first word.
        var offs = [], keys = [];
        var pos = 0, n = 0;
        while (pos < forms.length) {
            if (n % block === 0) {
                offs.push(pos);
                var bar = forms.indexOf('|', pos);
                keys.push(forms.slice(pos + 1, bar));
            }
            var nl = forms.indexOf('\n', pos);
            if (nl < 0) break;
            pos = nl + 1; n++;
        }
        var homo = new Map();
        text.slice(hStart + 4, sStart).split('\n').forEach(function (line) {
            if (!line) return;
            var tab = line.indexOf('\t');
            homo.set(line.slice(0, tab), JSON.parse(line.slice(tab + 1)));
        });
        var suffix = new Map(), maxSuf = 0;
        text.slice(sStart + 4).split('\n').forEach(function (line) {
            if (!line) return;
            var tab = line.indexOf('\t');
            var s = line.slice(0, tab);
            suffix.set(s, line.slice(tab + 1));
            if (s.length > maxSuf) maxSuf = s.length;
        });
        return { lang: meta.lang, meta: meta, forms: forms, offs: offs, keys: keys, block: block,
                 homo: homo, suffix: suffix, maxSuf: maxSuf, count: n };
    }

    // Binary search over block heads, then walk the block (front-coded).
    function lookupForm(dict, key) {
        var keys = dict.keys, lo = 0, hi = keys.length - 1, b = -1;
        while (lo <= hi) {
            var mid = (lo + hi) >> 1;
            if (keys[mid] <= key) { b = mid; lo = mid + 1; } else hi = mid - 1;
        }
        if (b < 0) return null;
        var f = dict.forms, pos = dict.offs[b], end = b + 1 < dict.offs.length ? dict.offs[b + 1] : f.length;
        var prev = '';
        while (pos < end) {
            var nl = f.indexOf('\n', pos);
            var bar = f.indexOf('|', pos);
            var plen = f.charCodeAt(pos) - 48;
            var word = prev.slice(0, plen) + f.slice(pos + 1, bar);
            if (word === key) {
                var code = f.slice(bar + 1, nl);
                var colon = code.indexOf(':');
                return { code: colon < 0 ? code : code.slice(0, colon), feats: colon < 0 ? '' : code.slice(colon + 1) };
            }
            if (word > key) return null;
            prev = word;
            pos = nl + 1;
        }
        return null;
    }

    function fromEnd(lang, base, code) {
        var idx = vowelIdx(lang, base), out = [];
        for (var i = 0; i < code.length; i++) {
            var k = +code[i];
            if (k >= 1 && k <= idx.length) out.push(idx[idx.length - k]);
        }
        return out.length ? out.sort(function (a, b) { return a - b; }) : null;
    }

    function guess(dict, base) {
        var low = base.toLowerCase(), idx = vowelIdx(dict.lang, low);
        if (idx.length < 2) return null;
        for (var k = Math.min(dict.maxSuf, low.length - 1); k >= 2; k--) {
            var lab = dict.suffix.get(low.slice(-k));
            if (lab === undefined) continue;
            if (lab === 'F') return [idx[0]];
            var e = +lab;
            return e <= idx.length ? [idx[idx.length - e]] : null;
        }
        return null;
    }

    function hasFeat(feats, f) {
        for (var i = 0; i + 1 < feats.length; i += 2) if (feats.substr(i, 2) === f) return true;
        return false;
    }
    function shareFeat(a, b) {
        for (var i = 0; i + 1 < a.length; i += 2) if (hasFeat(b, a.substr(i, 2))) return true;
        return false;
    }
    function posHas(p, set) {
        if (!p) return false;
        var parts = p.split(',');
        for (var i = 0; i < parts.length; i++) if (set[parts[i]]) return true;
        return false;
    }

    /* ── one word ────────────────────────────────────────────────────────── */

    // Strip the user's own marks; return base + manual stress positions.
    function splitMarks(lang, raw) {
        var base = '', manual = [];
        for (var i = 0; i < raw.length; i++) {
            var ch = raw[i];
            if (ch === ACUTE) { if (base && isVowel(lang, base[base.length - 1])) manual.push(base.length - 1); continue; }
            if (ch === '̀') continue;
            if (ch === '+') { var nx = raw[i + 1]; if (nx && isVowel(lang, nx)) manual.push(base.length); continue; }
            base += ch;
        }
        return { base: base, manual: manual };
    }

    function candidates(dict, base, sentStart) {
        var key = normKey(base);
        var rec = dict.homo.get(key);
        if (rec) {
            var rs = rec.r.slice();
            var cap = base[0] !== base[0].toLowerCase();
            if (cap && !sentStart) {
                var capped = rs.filter(function (r) { return r.c; });
                if (capped.length) rs = capped.concat(rs.filter(function (r) { return !r.c; }));
            } else if (!cap) {
                var common = rs.filter(function (r) { return !r.c; });
                if (common.length) rs = common;
            }
            return { kind: 'homo', readings: rs, frequent: !!rec.t };
        }
        var hit = lookupForm(dict, key);
        if (hit) {
            var s = fromEnd(dict.lang, key, hit.code);
            if (s) return { kind: 'dict', stress: s, feats: hit.feats };
        }
        return null;
    }

    /* ── text ────────────────────────────────────────────────────────────── */

    /**
     * analyze(dict, text) → tokens
     *   { t:'g', text }                                     gap (punctuation, spaces)
     *   { t:'w', text, start, end, base, stress:[i]|null,
     *     src:'manual'|'dict'|'homo'|'guess'|'mono'|'none',
     *     readings:[{s,p,c,f,l}]  (homographs, best first), pick:int,
     *     rule:'func'|'prep'|'loc'|'agree'|'case'|null, feats }
     */
    function analyze(dict, text) {
        var lang = dict.lang, out = [], last = 0, m;
        var sentStart = true, prev = null;
        WORD_RE.lastIndex = 0;
        while ((m = WORD_RE.exec(text))) {
            var gap = text.slice(last, m.index);
            if (gap) {
                out.push({ t: 'g', text: gap });
                if (/[.!?…][»"”)\]]*\s*$|\n\s*$/.test(gap)) sentStart = true;
                if (!/^[\s,;:«»"„“”'()—–-]*$/.test(gap)) prev = null;   // a number or symbol breaks agreement
            } else if (last === 0) sentStart = true;
            var raw = m[0];
            var tok = word(dict, raw, sentStart, prev, lang);
            tok.start = m.index; tok.end = m.index + raw.length;
            out.push(tok);
            prev = tok;
            sentStart = false;
            last = m.index + raw.length;
            // Hyphenated compound: "будь-який" may be one dictionary entry.
            if (text[last] === '-' && new RegExp('^[' + LETTER + ']').test(text.slice(last + 1, last + 2))) {
                var m2 = new RegExp('^-[' + LETTER + '][' + LETTER + '\'’ʼ`‘́+]*').exec(text.slice(last));
                if (m2) {
                    var joined = splitMarks(lang, raw + m2[0]);
                    if (!joined.manual.length && (dict.homo.get(normKey(joined.base)) || lookupForm(dict, normKey(joined.base)))) {
                        var tj = word(dict, raw + m2[0], tok.sentStart, null, lang);
                        tj.start = m.index; tj.end = last + m2[0].length;
                        out[out.length - 1] = tj;
                        last = tj.end;
                        WORD_RE.lastIndex = last;
                    }
                }
            }
        }
        if (last < text.length) out.push({ t: 'g', text: text.slice(last) });
        return out;
    }

    function word(dict, raw, sentStart, prev, lang) {
        var sp = splitMarks(lang, raw);
        var tok = { t: 'w', text: raw, base: sp.base, stress: null, src: 'none', readings: null, pick: 0, rule: null,
                    feats: '', sentStart: sentStart };
        var idx = vowelIdx(lang, sp.base);
        if (!idx.length) { tok.src = 'novowel'; return tok; }
        if (sp.manual.length) { tok.stress = sp.manual; tok.src = 'manual'; }
        var c = candidates(dict, sp.base, sentStart);
        if (c && c.kind === 'homo') {
            tok.readings = c.readings;
            var pick = 0, rule = null, rs = c.readings;
            if (rs.length > 1) {
                var low = sp.base.toLowerCase();
                if (c.frequent) {
                    var fi = firstIndex(rs, function (r) { return posHas(r.p, FUNC_POS); });
                    if (fi > 0) { pick = fi; rule = 'func'; }
                }
                if (!rule && prev && prev.t === 'w' && GEN_PREPS[lang][prev.base.toLowerCase()]) {
                    var gi = firstIndex(rs, function (r) { return r.f && hasFeat(r.f, 'Gs'); });
                    var hasOther = rs.some(function (r) { return !(r.f && hasFeat(r.f, 'Gs')); });
                    if (gi >= 0 && hasOther && gi !== 0) { pick = gi; rule = 'prep'; }
                    else if (gi === 0 && hasOther) rule = 'prep';
                }
                // «в то́му, що» vs «тому́ що»: after a locative preposition the
                // inflected reading beats the adverb/conjunction one.
                if (!rule && prev && prev.t === 'w' && LOC_PREPS[lang][prev.base.toLowerCase()]) {
                    var fits = function (r) { return r.f && (/[AL][sp]/.test(r.f)) && !posHas(r.p, INVARIABLE_POS); };
                    var li = firstIndex(rs, fits);
                    if (li > 0 && !fits(rs[0])) { pick = li; rule = 'loc'; }
                }
                if (!rule && prev && prev.t === 'w' && prev.feats) {
                    var ai = firstIndex(rs, function (r) { return r.f && shareFeat(r.f, prev.feats); });
                    var anyBad = rs.some(function (r) { return !(r.f && shareFeat(r.f, prev.feats)); });
                    if (ai >= 0 && anyBad) { if (ai !== 0) pick = ai; rule = 'agree'; }
                }
                if (!rule && rs[0].c && sp.base[0] !== low[0] && !sentStart) rule = 'case';
            }
            tok.pick = pick; tok.rule = rule;
            var r = rs[pick];
            tok.feats = (r.f && posHas(r.p, AGREE_POS)) ? r.f : '';
            if (!tok.stress) { tok.stress = r.s.slice(); tok.src = rs.length > 1 ? 'homo' : 'dict'; }
            return tok;
        }
        if (c && c.kind === 'dict') {
            tok.feats = c.feats || '';
            if (!tok.stress) { tok.stress = c.stress; tok.src = idx.length === 1 ? 'mono' : 'dict'; }
            return tok;
        }
        if (tok.stress) return tok;
        if (idx.length === 1) { tok.stress = [idx[0]]; tok.src = 'mono'; return tok; }
        var g = guess(dict, sp.base);
        if (g) { tok.stress = g; tok.src = 'guess'; }
        return tok;
    }

    function firstIndex(arr, fn) { for (var i = 0; i < arr.length; i++) if (fn(arr[i])) return i; return -1; }

    /* ── output ──────────────────────────────────────────────────────────── */

    /**
     * render(tokens, opts) → string
     *   opts.format   'acute' (U+0301 after the vowel, default) · 'plus' ('+' before —
     *                 Silero, Yandex SpeechKit) · 'plusafter' ('+' after — StyleTTS2-uk,
     *                 the UNLP benchmark) · 'caps' (stressed vowel upper-case)
     *   opts.mono     also mark one-syllable words (default false: nothing to learn there)
     *   opts.guesses  include ending-model guesses (default true)
     *   opts.onlyAmbiguous  mark only homographs (editors' mode)
     */
    function render(tokens, opts) {
        opts = opts || {};
        var fmt = opts.format || 'acute', out = '';
        for (var i = 0; i < tokens.length; i++) {
            var t = tokens[i];
            if (t.t !== 'w') { out += t.text; continue; }
            out += markWord(t, fmt, opts);
        }
        return out;
    }

    function shouldMark(t, opts) {
        if (!t.stress || !t.stress.length) return false;
        if (t.src === 'mono' && !opts.mono) return false;
        if (t.src === 'guess' && opts.guesses === false) return false;
        if (opts.onlyAmbiguous && t.src !== 'homo' && !(t.src === 'manual' && t.readings && t.readings.length > 1)) return false;
        return true;
    }

    function markWord(t, fmt, opts) {
        if (!shouldMark(t, opts)) return t.base;
        var set = {}, s = '';
        t.stress.forEach(function (i) { set[i] = 1; });
        var allCaps = t.base.length > 1 && t.base === t.base.toUpperCase();
        for (var i = 0; i < t.base.length; i++) {
            var ch = t.base[i];
            if (!set[i]) { s += ch; continue; }
            if (fmt === 'plus') s += '+' + ch;
            else if (fmt === 'plusafter') s += ch + '+';
            else if (fmt === 'caps') s += allCaps ? ch + ACUTE : ch.toUpperCase();
            else s += ch + ACUTE;
        }
        return s;
    }

    /* ── marks for files (a .docx run, a subtitle line) ──────────────────── */

    // Index in `raw` of the character that is base[b] — raw may carry marks
    // splitMarks() removed (U+0301, U+0300, '+' before a vowel).
    function rawIndex(lang, raw, b) {
        var k = -1;
        for (var i = 0; i < raw.length; i++) {
            var ch = raw[i];
            if (ch === ACUTE || ch === '̀') continue;
            if (ch === '+' && raw[i + 1] && isVowel(lang, raw[i + 1])) continue;
            if (++k === b) return i;
        }
        return -1;
    }

    /**
     * stressOffsets(tokens, opts, lang) → sorted absolute offsets (into the text that
     * was analyzed) of every vowel render() would mark. Words that already carry the
     * author's own mark are skipped: their mark is in the text and stays as it is.
     */
    function stressOffsets(tokens, opts, lang) {
        opts = opts || {};
        var out = [];
        for (var i = 0; i < tokens.length; i++) {
            var t = tokens[i];
            if (t.t !== 'w' || t.src === 'manual' || !shouldMark(t, opts)) continue;
            for (var j = 0; j < t.stress.length; j++) {
                var r = rawIndex(lang, t.text, t.stress[j]);
                if (r >= 0) out.push(t.start + r);
            }
        }
        return out.sort(function (a, b) { return a - b; });
    }

    // One vowel in the chosen format.
    function markChar(ch, fmt, allCaps) {
        if (fmt === 'plus') return '+' + ch;
        if (fmt === 'plusafter') return ch + '+';
        if (fmt === 'caps') return allCaps ? ch + ACUTE : ch.toUpperCase();
        return ch + ACUTE;
    }

    // Apply offsets to a string that starts at absolute offset `from`.
    function applyOffsets(str, from, offsets, fmt) {
        if (!offsets.length) return str;
        var out = '', last = 0;
        for (var i = 0; i < offsets.length; i++) {
            var at = offsets[i] - from;
            if (at < 0 || at >= str.length) continue;
            out += str.slice(last, at) + markChar(str[at], fmt, false);
            last = at + 1;
        }
        return out + str.slice(last);
    }

    /**
     * detectLang(text) → 'uk' | 'be' | 'ru' | null. Belarusian — this tool's
     * own question — from letters: ў, or ы/э with і, or і with no и/щ.
     * Ukrainian or Russian — the site's one detector (window.__cyrillicLang).
     * null = not enough evidence, or Cyrillic of another language.
     */
    function detectLang(text) {
        var s = text.length > 6000 ? text.slice(0, 6000) : text, c = {}, cyr = 0;
        for (var i = 0; i < s.length; i++) {
            var ch = s[i].toLowerCase();
            if (ch >= 'а' && ch <= 'я' || 'ёіїєґў'.indexOf(ch) >= 0) { cyr++; c[ch] = (c[ch] || 0) + 1; }
        }
        if (cyr < 12) return null;
        var n = function (l) { var k = 0; for (var j = 0; j < l.length; j++) k += c[l[j]] || 0; return k; };
        var uk = n('їєґ'), be = n('ў'), I = n('і'), Y = n('ыэ'), II = n('ищ');
        // і without и/щ: Ukrainian has и in nearly every sentence, Belarusian never.
        if (be || (Y && I && !uk) || (I && !II && !uk && cyr >= 20)) return 'be';
        // Ukrainian or Russian: the site's one detector (timbrica-core), «don't know» on no sign —
        // its function words and endings tell «Садок вишневий коло хати», which letters alone could not.
        var G = typeof globalThis !== 'undefined' ? globalThis : (typeof self !== 'undefined' ? self : {});
        var cy = typeof G.__cyrillicLang === 'function' ? G.__cyrillicLang(s, { fallback: null }) : null;
        return cy === 'uk' || cy === 'ru' ? cy : null;
    }

    /* ── helpers for the word card ───────────────────────────────────────── */

    // Syllables by the school rule: one vowel per syllable; a lone consonant opens
    // the next syllable; in a cluster a sonorant (й в л м н р ў) closes the previous one.
    function syllables(lang, base) {
        var idx = vowelIdx(lang, base);
        if (idx.length < 2) return [base];
        var cuts = [], SON = 'йвлмнрўЙВЛМНРЎ';
        for (var v = 0; v + 1 < idx.length; v++) {
            var a = idx[v], b = idx[v + 1];
            var cons = [];
            for (var k = a + 1; k < b; k++) if (/[^'’ʼ`‘ьЬ]/.test(base[k])) cons.push(k);
            var cut;
            if (cons.length <= 1) cut = cons.length ? cons[0] : b;
            else cut = SON.indexOf(base[cons[0]]) >= 0 ? cons[1] : cons[0];
            // soft sign and apostrophe stay with the consonant before them
            while (cut > a + 1 && /['’ʼ`‘ьЬ]/.test(base[cut])) cut++;
            cuts.push(cut);
        }
        var out = [], p = 0;
        cuts.forEach(function (c) { out.push(base.slice(p, c)); p = c; });
        out.push(base.slice(p));
        return out;
    }

    // Which syllable (1-based) carries the stress at char index `i`.
    function syllableNo(lang, base, i) {
        var idx = vowelIdx(lang, base);
        var k = idx.indexOf(i);
        return k < 0 ? 0 : k + 1;
    }

    return {
        parseDict: parseDict, lookupForm: lookupForm, analyze: analyze, render: render,
        markWord: markWord, shouldMark: shouldMark, splitMarks: splitMarks, syllables: syllables,
        syllableNo: syllableNo, vowelIdx: vowelIdx, normKey: normKey, guess: guess,
        stressOffsets: stressOffsets, applyOffsets: applyOffsets, markChar: markChar,
        detectLang: detectLang, VOWELS: VOWELS, ACUTE: ACUTE
    };
});
